<script setup lang="ts">
import { inject, ref } from "vue";
import { useTranslation } from "i18next-vue";
import DRDialog from "@/components/UI/feedbacks/DRDialog.vue";
import PhosphorIcon from "@/components/UI/PhosphorIcon.vue";
import { isToolHintVisible } from "@/stores/CanvasViewState";
import { IN_PANEL_DIALOG } from "./drawerMenuContext";

/**
 * Coquille commune des onglets du volet droit. L'explication de l'onglet n'est
 * plus affichée en permanence : elle se lit à la demande via le bouton d'aide
 * posé à côté du titre, ce qui rend le volet aux listes. Le slot `header`
 * reste fixe sous le titre (sous-onglets d'un volet), hors de la zone qui
 * défile.
 */

withDefaults(
  defineProps<{
    label: string;
    /** Texte explicatif de l'onglet, montré dans la popup d'aide */
    description?: string;
    actions?: boolean;
  }>(),
  {
    actions: false,
  },
);

const { t } = useTranslation();

const showHelp = ref(false);

// En modale, l'aide contextuelle (bas droite de l'écran) ne recouvre pas le pied.
const inPanelDialog = inject(IN_PANEL_DIALOG, false);

const scrollArea = ref<HTMLElement | null>(null);

/** Revient en haut de la liste (contenu remplacé : filtre, catégorie…). */
function scrollToTop() {
  scrollArea.value?.scrollTo({ top: 0 });
}

defineExpose({ scrollToTop });
</script>

<template>
  <div class="w-100 h-100 text-white d-flex flex-column">
    <div class="p-3 title-row">
      <div class="fw-medium text-center">{{ label }}</div>
      <!-- Hors flux : le titre reste centré sur la largeur du volet -->
      <button
        v-if="description"
        type="button"
        class="help-btn"
        @click="showHelp = true"
      >
        <PhosphorIcon name="question" :size="11" />
        <span>{{ t("help.title") }}</span>
      </button>
    </div>
    <div v-if="$slots.header" class="px-3 pb-2"><slot name="header"></slot></div>
    <div ref="scrollArea" class="col scroll-y">
      <div class="p-3"><slot></slot></div>
    </div>
    <!-- L'aide contextuelle (bas droite) recouvre le pied : on le remonte -->
    <div
      v-if="actions"
      class="col-auto actions-footer"
      :class="{ 'above-tool-hint': isToolHintVisible && !inPanelDialog }"
    >
      <div class="px-3 pb-3 pt-1"><slot name="actions"></slot></div>
    </div>

    <!-- canvas-dialog : couleurs de l'outil dessin, une modale blanche jurerait -->
    <DRDialog
      v-model="showHelp"
      class="canvas-dialog"
      :title="label"
      hide-footer
      :width="440"
    >
      <p class="help-text">{{ description }}</p>
    </DRDialog>
  </div>
</template>

<style lang="scss" scoped>
.title-row {
  position: relative;
}

.help-btn {
  position: absolute;
  top: 50%;
  right: 10px;
  transform: translateY(-50%);
  display: inline-flex;
  align-items: center;
  gap: 2px;
  padding: 0;
  border: none;
  background: none;
  color: rgba(255, 255, 255, 0.35);
  font-size: 10px;
  cursor: pointer;
  transition: color 0.15s ease;

  &:hover {
    color: rgba(255, 255, 255, 0.8);
  }
}

.actions-footer {
  transition: padding-bottom 0.15s ease;

  // Hauteur du cartouche d'aide (une ligne de 16px + padding + bordure)
  &.above-tool-hint {
    padding-bottom: 24px;
  }
}

.help-text {
  margin: 0;
  white-space: pre-line;
  line-height: 1.5;
  color: inherit;
}

.scroll-y {
  overflow-y: auto;
}

.scroll-y::-webkit-scrollbar {
  width: 8px;
}

.scroll-y::-webkit-scrollbar-thumb {
  background-color: rgba(255, 255, 255, 0.5);
  border-radius: 4px;
}

.scroll-y::-webkit-scrollbar-thumb:hover {
  background-color: rgba(255, 255, 255, 0.7);
}

.scroll-y::-webkit-scrollbar-track {
  background-color: rgba(0, 0, 0, 0.2);
  border-radius: 4px;
}
</style>
